/**
 * Conversation — tela de conversa estilo WhatsApp.
 *
 * Para conversas de alerta: três abas (Alertas | Conversas | Atendente IA).
 * Para conversas humanas (dm/org): thread único sem abas, com composer e
 * (para org) painel de integrantes + opção de silenciar.
 *
 * Rotas: /chat/:conversationId (a página `Conversation`, que lê a rota). A conversa em si é
 * `ConversaDoChat`, que recebe tudo por props: o painel do @DBA no editor SQL usa a mesma
 * (consoleai#26057), sem navegar.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Box,
  Button,
  CloseButton,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconArchive, IconArrowLeft, IconBell, IconBellOff, IconCamera, IconChevronDown, IconChevronUp, IconDots, IconSearch, IconServerCog, IconSparkles, IconUsers } from '@tabler/icons-react'
import {
  mandaAoAtendente,
  textoDoInicioDeAtendimento,
  textoDoFinalizarChamado,
  type EstadoDeInicioDeAtendimento,
} from './atendimentoChamado'
import { computeMatches, stepMatch, remapIndex } from './messageMatch'
import { buildAlertQuestion } from './alertQuestion'
import { searchMessages } from '@/lib/messageSearch'
import { useConversationMessages, useConversations, useAskBotStream, useAskRoboNoGrupo, useHideConversation } from '@/hooks/useChat'
import { useTypingIndicator } from '@/hooks/useTyping'
import { useChatRealtime } from '@/hooks/useChatRealtime'
import { useToggleMute } from '@/hooks/useChatDirectory'
import { chatApi } from '@/api/chat'
import type { AskAttachmentRef, ChatMessage, ChatTab } from '@/api/chat'
import { useAuthStore } from '@/store/auth'
import { AlertBubble } from './components/AlertBubble'
import { HumanBubble } from './components/HumanBubble'
import { ActionProposalCard, type ProposedAction } from './components/ActionProposalCard'
import { cartaoDoAviso } from './cartaoDoAviso'
import { ancorasPorStage, cartoesDosPedidos, type AncoraDoStage } from './pedidosDaConversa'
import { PainelDoDevOps } from './components/PainelDoDevOps'
import {
  DiagnosticoCard,
  type LaudoDiagnostico,
  type MarcaDiscordancia,
} from './components/DiagnosticoCard'
import { FontesConsultadas, lerFontes } from '@/components/kb/FontesConsultadas'
import { lerRegraDeOrdem } from './components/AlternativasDoLaudo'
import { Composer } from './components/Composer'
import { DaySeparator } from './components/DaySeparator'
import { LiveAssistantBubble } from './components/LiveAssistantBubble'
import type { PassoAoVivo } from './passosAoVivo'
import { MembersDrawer } from './components/MembersDrawer'
import { OfertaDeEncaminhamento } from './robo/OfertaDeEncaminhamento'
import { RoboDmPortao } from './robo/RoboDmPortao'
import { RoboDmTopo } from './robo/RoboDmTopo'
import { useRecomecarAtendente } from './components/RecomecarAtendente'
import { useRoboAsk, type Robo } from '@/api/robo'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth() &&
    a.getDate()     === b.getDate()
  )
}

// ---------------------------------------------------------------------------
// Sub-componente: lista de mensagens de uma aba
// ---------------------------------------------------------------------------

interface MessageListProps {
  cid: number
  tab?: ChatTab
  onExplainAlert?: (id: number) => void
  onReply: (msg: ChatMessage) => void
  /**
   * Arma o composer para a próxima mensagem ir ao @Atendente.
   *
   * Existe porque o bot faz perguntas ("Posso abrir um chamado?", "em qual tenant?")
   * e, desde que a aba dele foi aposentada em 23/07/2026, resposta sem menção
   * explícita vai para o canal do TIME — o bot nunca a recebe. Medido em produção em
   * 06/08: dois `POST /conversations/72/messages` onde deveria haver `ask/stream`, e o
   * fluxo de abertura de chamado parou no passo de confirmação.
   * Ver docs/CHAT_TRIAGEM_CHAMADO_SPEC.md RF-002 e INV-1.
   */
  onReplyToAtendente?: () => void
  /** Resposta de robô de colega no grupo: a réplica vai a ELE (consoleai#24585). */
  onReplyToRobo?: (alvo: AlvoRobo) => void
  /** Id da mensagem que deve rolar para a viewport e piscar (busca in-view / global). */
  focusMessageId?: number | null
  /** Callback: reporta a lista de mensagens carregadas ao pai (para o contador da busca). */
  onMessagesLoaded?: (messages: ChatMessage[]) => void
  /** Ids das mensagens que casam com a busca in-view (destaque persistente enquanto a barra está aberta). */
  matchIds?: number[]
  /**
   * Callback disparado UMA vez, após o `focusMessageId` ser efetivamente rolado
   * para a viewport (elemento encontrado no mapa de refs). Permite ao pai
   * consumir o latch de foco global one-shot, para que mudanças posteriores em
   * `messages.length` (mensagem ao vivo / página antiga) não re-disparem o scroll.
   */
  onFocusConsumed?: (id: number) => void
  /**
   * Quando não-nulo, exibe a `LiveAssistantBubble` no fim da lista (aba
   * Atendente, request em voo): mostra os passos de ferramenta ao vivo + o
   * indicador "…digitando". `null`/`undefined` = não está streamando.
   */
  assistantSteps?: PassoAoVivo[] | null
  /**
   * Aba/painel visível no momento. Ao passar de oculto → visível, rola pro fim
   * (ex.: "Explicar" troca p/ a aba Atendente e deve mostrar a última mensagem).
   * `undefined` = layout de thread único (sempre visível).
   */
  active?: boolean
}

export interface AlvoRobo {
  botUserId: number
  nome: string
}

/** O robô que escreveu a mensagem, como `robo_grupo_routes.py` grava a resposta. */
function roboDaMensagem(msg: ChatMessage): AlvoRobo | null {
  const meta = msg.metadata
  if (!meta || meta.agent !== 'robo') return null
  const robo = meta.robo as { bot_user_id?: unknown } | undefined
  const id = robo?.bot_user_id
  if (typeof id !== 'number') return null
  return { botUserId: id, nome: (msg.author_name ?? 'o robô').split(' · ')[0] }
}

function MessageList({ cid, tab, onExplainAlert, onReply, onReplyToAtendente, onReplyToRobo, focusMessageId, onMessagesLoaded, matchIds, onFocusConsumed, assistantSteps, active }: MessageListProps) {
  const qc = useQueryClient()
  const myId = useAuthStore((s) => s.user)?.id ?? null
  const { data: msgData, isLoading } = useConversationMessages(cid, tab)
  const viewportRef = useRef<HTMLDivElement>(null)
  // Conversa cujo "pulo pro fim" inicial já foi feito (uma vez por conversa).
  const initialScrolledCidRef = useRef<number | null>(null)
  // Mapa de refs por id de mensagem — base do mecanismo scroll+highlight (A e B).
  const msgRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [flashId, setFlashId] = useState<number | null>(null)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const messages: ChatMessage[] = msgData
    ? [...msgData.pages.flatMap((p) => p.items)].reverse()
    : []

  // Conversa 1:1 (DM) → oculta avatar/nome por mensagem (estilo WhatsApp; a
  // identidade do par já está no cabeçalho). Em grupo (org) mantém.
  const isDmConversation = msgData?.pages?.[0]?.conversation?.conv_type === 'dm'

  const matchSet = useMemo(() => new Set(matchIds ?? []), [matchIds])

  // Reporta as mensagens carregadas ao pai (para o contador/navegação da busca).
  useEffect(() => {
    onMessagesLoaded?.(messages)
  }, [msgData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mecanismo reutilizável scroll+highlight (usado por A: global e B: busca in-view).
  // Rola a mensagem-alvo para a viewport e aplica um flash temporário (~2s).
  // Devolve `true` se a mensagem estava carregada (ref presente) e o scroll
  // ocorreu; `false` (no-op gracioso) caso contrário, para permitir retry.
  const scrollAndHighlight = useCallback((id: number): boolean => {
    const el = msgRefs.current.get(id)
    const found = !!(el && typeof el.scrollIntoView === 'function')
    if (found) {
      el!.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setFlashId(id)
    if (flashTimer.current) clearTimeout(flashTimer.current)
    flashTimer.current = setTimeout(() => setFlashId(null), 2000)
    return found
  }, [])

  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current)
  }, [])

  // Latch one-shot do foco: guarda o último id JÁ consumido (rolado). Enquanto
  // um id não foi rolado com sucesso (mensagem ainda não carregada), o efeito
  // re-tenta a cada mudança de messages.length. Assim que rola de fato, marca
  // como consumido e avisa o pai (onFocusConsumed) — evitando que uma nova
  // mensagem ao vivo re-dispare o scroll para o alvo antigo. Um id NOVO (global
  // diferente ou nova ocorrência da busca) reinicia o ciclo naturalmente.
  const consumedFocusRef = useRef<number | null>(null)
  // O foco acabou de ser consumido (o pai zera o id): a volta do id a `null` não pode
  // disparar os pulos para o fim abaixo, senão eles desfazem o scroll até o alvo — a
  // busca global e o "Ir ao cartão" do painel do @DevOps (spec 2026-11-04 D-5).
  const soltouFocoRef = useRef(false)

  // (A) + (B): quando o pai muda o id em foco, rola + pisca (assim que a
  // mensagem estiver carregada). Depende também de messages.length para
  // re-tentar após a lista carregar — mas só até o id atual ser consumido.
  useEffect(() => {
    if (focusMessageId == null) {
      // Sem foco, o latch não guarda nada: pedir o MESMO id de novo (dois cliques no
      // "Ir ao cartão") rola de novo.
      consumedFocusRef.current = null
      return
    }
    if (consumedFocusRef.current === focusMessageId) return // já rolado uma vez
    const scrolled = scrollAndHighlight(focusMessageId)
    if (scrolled) {
      consumedFocusRef.current = focusMessageId
      soltouFocoRef.current = true
      onFocusConsumed?.(focusMessageId)
    }
    // Se não rolou (mensagem ainda não carregada), NÃO consome: re-tenta quando
    // messages.length mudar (página carregada).
  }, [focusMessageId, messages.length, scrollAndHighlight, onFocusConsumed])

  // Ao ENTRAR na conversa: pula pro FIM instantaneamente assim que as mensagens
  // carregam (sem smooth, sem depender de nova mensagem) — uma vez por conversa.
  // Reforço com timeout p/ acomodar layout/autosize/imagens.
  useEffect(() => {
    if (focusMessageId != null) return
    if (isLoading || messages.length === 0) return
    if (initialScrolledCidRef.current === cid) return
    initialScrolledCidRef.current = cid
    const jump = () => {
      const el = viewportRef.current
      if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight })
    }
    jump()
    const t = setTimeout(jump, 60)
    return () => clearTimeout(t)
  }, [cid, isLoading, messages.length, focusMessageId])

  // Ao ATIVAR a aba (oculto → visível, ex.: "Explicar" leva p/ a aba Atendente),
  // rola pro fim para mostrar a última mensagem. Painel oculto não tem layout
  // (scrollHeight indisponível), por isso o scroll do "enter" pode não pegar —
  // este efeito garante ao ficar visível. `active === false` = oculto → ignora.
  useEffect(() => {
    if (active === false) return
    if (focusMessageId != null) return
    if (soltouFocoRef.current) return
    const jump = () => {
      const el = viewportRef.current
      if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight })
    }
    jump()
    const t = setTimeout(jump, 60)
    return () => clearTimeout(t)
  }, [active, focusMessageId])

  // Auto-scroll para o fim sempre que as mensagens mudarem OU enquanto o
  // Atendente streama (a LiveAssistantBubble cresce com os passos) — exceto com
  // foco de busca ativo (senão sobrescreveria o scroll para a ocorrência).
  // Faz um jump IMEDIATO (garante que a mensagem otimista recém-inserida pelo
  // remetente apareça na hora, sem depender do smooth que às vezes não pega
  // antes do layout) + reforço smooth logo em seguida.
  useEffect(() => {
    if (focusMessageId != null) return
    if (soltouFocoRef.current) {
      // O último efeito da soltura do foco: a próxima mensagem volta a rolar para o fim.
      soltouFocoRef.current = false
      return
    }
    const el = viewportRef.current
    if (!el || typeof el.scrollTo !== 'function') return
    el.scrollTo({ top: el.scrollHeight })
    const t = setTimeout(() => {
      const v = viewportRef.current
      if (v && typeof v.scrollTo === 'function') {
        v.scrollTo({ top: v.scrollHeight, behavior: 'smooth' })
      }
    }, 50)
    return () => clearTimeout(t)
  }, [messages.length, assistantSteps, focusMessageId])

  // Marcar como lido ao montar / quando chegarem novas mensagens.
  // Sem message_id: o servidor marca a conversa INTEIRA (MAX, todas as abas) —
  // comportamento WhatsApp. Zera o badge de vez, incluindo alertas de abas que
  // o usuário não abriu (o unread_count da lista é global à conversa).
  useEffect(() => {
    if (messages.length === 0) return
    chatApi.markRead(cid)
      .then(() => {
        // Zera o badge "não visualizado" nas DUAS listas (alertas + DMs/grupos).
        // Sem isso o contador ficava preso até um refetch manual.
        void qc.invalidateQueries({ queryKey: ['conversations'] })
        void qc.invalidateQueries({ queryKey: ['chat-human-conversations'] })
      })
      .catch(() => {
        // best-effort: ignora erros silenciosamente
      })
  }, [cid, messages.length]) // eslint-disable-line react-hooks/exhaustive-deps

  type Item =
    | { type: 'separator'; date: Date; key: string }
    | { type: 'message'; msg: ChatMessage; startsGroup: boolean }

  const items: Item[] = []
  let lastDay: Date | null = null
  let prevAuthor: number | null | undefined = undefined
  let prevTime = 0

  for (const msg of messages) {
    const d = new Date(msg.created_at)
    const t = d.getTime()
    let sep = false
    if (!lastDay || !isSameDay(d, lastDay)) {
      items.push({ type: 'separator', date: d, key: `sep-${msg.id}` })
      lastDay = d
      sep = true
    }
    // Início de grupo (estilo WhatsApp): novo dia, troca de remetente, ou
    // intervalo > 5 min desde a mensagem anterior. Mensagens seguidas do mesmo
    // autor ficam "coladas" (espaçamento menor, sem repetir avatar/nome).
    const startsGroup =
      sep ||
      prevAuthor !== msg.author_user_id ||
      (prevTime > 0 && t - prevTime > 5 * 60 * 1000)
    items.push({ type: 'message', msg, startsGroup })
    prevAuthor = msg.author_user_id
    prevTime = t
  }

  const emptyLabel =
    tab === 'conversas'
      ? 'Nenhuma mensagem de equipe nesta conversa ainda.'
      : tab === 'alertas'
      ? 'Nenhum alerta nesta conversa ainda.'
      : tab === 'atendente'
      ? 'Ainda não te respondi por aqui — me pergunte algo!'
      : 'Nenhuma mensagem nesta conversa ainda.'

  return (
    <ScrollArea
      flex={1}
      viewportRef={viewportRef}
      p="md"
      scrollbars="y"
      style={{ minHeight: 0 }}
    >
      <Stack gap={2} aria-live="polite" aria-label="Mensagens">
        {isLoading && (
          <Group justify="center" py="xl">
            <Loader size="sm" type="dots" color="teal" />
            <Text size="sm" c="dimmed">Carregando…</Text>
          </Group>
        )}

        {!isLoading && messages.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {emptyLabel}
          </Text>
        )}

        {items.map((item) => {
          if (item.type === 'separator') {
            return <DaySeparator key={item.key} date={item.date} />
          }
          const { msg, startsGroup } = item
          const isFlash = flashId === msg.id
          const isMatch = matchSet.has(msg.id)
          // Envolve cada mensagem num contêiner com ref (mapa por id) + destaque.
          // - flash: pisca temporário (~2s) na ocorrência atual / alvo do global.
          // - match: fundo suave persistente enquanto a barra de busca estiver aberta.
          const setRef = (el: HTMLDivElement | null) => {
            if (el) msgRefs.current.set(msg.id, el)
            else msgRefs.current.delete(msg.id)
          }
          const highlightStyle: React.CSSProperties = {
            borderRadius: 8,
            // Agrupamento estilo WhatsApp: mais respiro só quando começa um novo
            // grupo (novo autor/dia/gap); mensagens seguidas do mesmo autor ficam
            // coladas (o Stack já dá 2px de base).
            marginTop: startsGroup ? 10 : 0,
            transition: 'background-color 0.4s ease',
            backgroundColor: isFlash
              ? 'var(--mantine-color-yellow-2, #ffec99)'
              : isMatch
              ? 'var(--mantine-color-yellow-0, #fff9db)'
              : 'transparent',
          }

          if (msg.kind === 'human') {
            // Alinhamento estilo WhatsApp à prova de falha: força o lado da linha
            // via flex (independe do CSS interno do HumanBubble). Mensagem própria
            // → direita; recebida → esquerda.
            const isOwnRow = msg.author_user_id != null && msg.author_user_id === myId
            return (
              <div key={msg.id} ref={setRef} style={highlightStyle}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: isOwnRow ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}
                >
                  <HumanBubble
                    cid={cid}
                    message={msg}
                    onReply={(m) => onReply(m)}
                    // DM sempre oculta; em grupo, oculta avatar/nome nas
                    // mensagens de continuação (só o 1º do grupo mostra).
                    hideIdentity={isDmConversation || !startsGroup}
                  />
                </div>
              </div>
            )
          }

          const meta =
            msg.metadata && typeof msg.metadata === 'object' && !Array.isArray(msg.metadata)
              ? (msg.metadata as Record<string, unknown>)
              : null
          const proposedAction =
            meta?.proposed_action &&
            typeof meta.proposed_action === 'object' &&
            !Array.isArray(meta.proposed_action)
              ? (meta.proposed_action as ProposedAction)
              : null
          // O laudo do @DevOps chega pelo MESMO trilho da proposta: metadata da
          // mensagem do bot. O cartão é o chão de fatos — a prosa acima dele não
          // o substitui.
          const diagnostico =
            meta?.diagnostico &&
            typeof meta.diagnostico === 'object' &&
            !Array.isArray(meta.diagnostico)
              ? (meta.diagnostico as LaudoDiagnostico)
              : null
          // A marca de "isso não bate" (TASK-10428) chega no MESMO metadata,
          // gravada pela rota de discordância. Ela é a única razão de o sinal
          // sobreviver a um reload — a trilha é para nós, esta marca é para quem
          // clicou.
          const discordancia =
            meta?.diagnostico_discordancia &&
            typeof meta.diagnostico_discordancia === 'object' &&
            !Array.isArray(meta.diagnostico_discordancia)
              ? (meta.diagnostico_discordancia as MarcaDiscordancia)
              : null
          // O que a busca da KB ENTREGOU ao modelo neste turno (TASK-9885), no
          // MESMO metadata. Só `document_id` e página: o título se resolve por
          // quem lê, porque a conversa pode ser de grupo (TASK-11825).
          const fontes = msg.kind === 'bot' ? lerFontes(meta?.fontes, meta?.fontes_total) : null
          // Laudo preparado por regra de ordem (spec 2026-09-30 §5.2): o número da regra e
          // os verbos dela, no MESMO metadata do laudo.
          const regraDeOrdem = lerRegraDeOrdem(meta?.regra_de_ordem)

          return (
            <div key={msg.id} ref={setRef} style={highlightStyle}>
              <AlertBubble
                cid={cid}
                message={msg}
                onExplain={
                  (msg.kind === 'alert' ||
                    (msg.kind === 'bot' && Boolean(meta?.recommendation_id))) &&
                  onExplainAlert
                    ? onExplainAlert
                    : undefined
                }
                onReply={(m) => onReply(m)}
              />
              {fontes && <FontesConsultadas {...fontes} ml={44} />}
              {msg.kind === 'bot' && diagnostico && (
                <DiagnosticoCard
                  laudo={diagnostico}
                  conversationId={cid}
                  messageId={msg.id}
                  discordancia={discordancia}
                  regraDeOrdem={regraDeOrdem}
                />
              )}
              {(msg.kind === 'bot' || cartaoDoAviso(msg.kind, proposedAction)) && proposedAction && (
                <ActionProposalCard
                  conversationId={cid}
                  messageId={msg.id}
                  proposedAction={proposedAction}
                />
              )}
              {msg.kind === 'bot' && meta?.oferta_encaminhamento != null && (
                // A recusa de produção que o dono pode pedir a quem pode (2D, TASK-12127).
                <Box ml={44}>
                  <OfertaDeEncaminhamento
                    cid={cid}
                    messageId={msg.id}
                    enviado={meta.encaminhamento as { pedido_id: number; destinatarios: number } | undefined}
                  />
                </Box>
              )}
              {msg.kind === 'bot' && !proposedAction && roboDaMensagem(msg) && onReplyToRobo && (
                // Quem respondeu foi o robô de um colega, não o @Atendente: a
                // réplica volta a ELE, pela rota do grupo (consoleai#24585).
                <Group gap="xs" mt={4} ml={44}>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="inovareTeal"
                    leftSection={<IconSparkles size={13} />}
                    onClick={() => onReplyToRobo(roboDaMensagem(msg)!)}
                  >
                    Responder a {roboDaMensagem(msg)!.nome}
                  </Button>
                </Group>
              )}
              {msg.kind === 'bot' && !proposedAction && !roboDaMensagem(msg) && !meta?.integracao && onReplyToAtendente && (
                // Mensagem de integração (`metadata.integracao`, consoleai#25341) não é
                // do @Atendente: não há a quem responder por este botão.
                // Sem cartão, a mensagem do bot costuma terminar em pergunta — e a
                // resposta precisa de caminho de volta (INV-1 da spec). Botão explícito
                // em vez de roteamento implícito: numa conversa compartilhada com o
                // time, para onde a mensagem vai tem de ficar visível.
                <Group gap="xs" mt={4} ml={44}>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="inovareTeal"
                    leftSection={<IconSparkles size={13} />}
                    onClick={onReplyToAtendente}
                  >
                    Responder ao @Atendente
                  </Button>
                </Group>
              )}
            </div>
          )
        })}

        {assistantSteps != null && <LiveAssistantBubble steps={assistantSteps} />}
      </Stack>
    </ScrollArea>
  )
}

// ---------------------------------------------------------------------------
// Helper de iniciais (para avatar de grupo/dm no cabeçalho)
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = (name || '').trim().split(/[\s-_]+/)
  return parts
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

/**
 * O que abre a conversa com um texto no campo, SEM enviar (a pessoa revisa e manda): a busca da
 * KB, o "Perguntar ao @DBA" do editor. `consultaSqlId` (spec 2026-10-30-agente-dba §4.9) vai no
 * PRÓXIMO envio, e só nele. `chave` muda a cada pedido: o mesmo texto pedido duas vezes reabre.
 */
export interface InicioDaConversa {
  prefill: string
  consultaSqlId?: number
  chave: number
}

export interface ConversaDoChatProps {
  cid: number
  inicial?: InicioDaConversa | null
  /** O botão do cabeçalho. Sem ele, não há botão (a casca decide o que "voltar" quer dizer). */
  voltar?: { rotulo: string; acao: () => void; icone?: ReactNode }
  /**
   * Fora da rota `/chat/:id` (o painel do editor): não consome o `location.state` da página que
   * a hospeda — o scroll da busca global e o início de chamado são pedidos feitos à ROTA do chat.
   */
  embutida?: boolean
}

/** A página `/chat/:conversationId`: lê a rota e passa à conversa. */
export default function Conversation() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [inicial, setInicial] = useState<InicioDaConversa | null>(null)
  const contador = useRef(0)

  // "Perguntar ao @Atendente" da busca na KB (spec 2026-10-20-kb-busca): o texto chega no campo,
  // SEM enviar. Limpa o state (reload não repete). O "Perguntar ao @DBA" do editor não passa por
  // aqui: abre a conversa no painel do próprio editor, com o id da consulta por prop.
  useEffect(() => {
    const texto = (location.state as { prefill?: string } | null)?.prefill
    if (typeof texto !== 'string' || !texto) return
    contador.current += 1
    setInicial({ prefill: texto, chave: contador.current })
    navigate(location.pathname, { replace: true, state: null })
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ConversaDoChat
      cid={Number(conversationId ?? 0)}
      inicial={inicial}
      voltar={{ rotulo: 'Voltar para a lista de conversas', acao: () => navigate('/chat') }}
    />
  )
}

export function ConversaDoChat({ cid, inicial, voltar, embutida = false }: ConversaDoChatProps) {
  // Tempo real dentro da conversa: esta é uma rota separada do ChatHome, que
  // é onde o useChatRealtime estava montado. Sem isto, mensagens novas e o
  // "digitando…" só apareciam ao sair/voltar (fix 2026-07-21). O hook reusa a
  // conexão SSE única (SharedWorker), então montar nas duas telas é seguro.
  useChatRealtime()

  const navigate = useNavigate()
  const location = useLocation()

  const { data: conversations } = useConversations()
  const askStream = useAskBotStream(cid)
  // A DM do robô pergunta pela rota dele, não pelo stream do @Atendente (spec 2026-10-02).
  const roboAsk = useRoboAsk(cid)
  const askRobo = useAskRoboNoGrupo(cid)
  const typingText = useTypingIndicator(cid)

  // Aba ativa — padrão: alertas
  const [activeTab, setActiveTab] = useState<ChatTab>('alertas')

  // Painel de integrantes
  const [membersOpen, setMembersOpen] = useState(false)

  // Texto pré-preenchido no Composer pelo botão "Explicar" de um AlertBubble.
  // O botão NÃO dispara o Atendente automaticamente — apenas troca de aba e
  // pré-preenche o campo. O usuário revisa e envia manualmente. O vínculo com o
  // alerta é preservado em `pendingAlertMessageId` e anexado ao próximo `ask`.
  const [prefill, setPrefill] = useState<string | undefined>(undefined)
  const [pendingAlertMessageId, setPendingAlertMessageId] = useState<number | undefined>(undefined)
  // "Perguntar ao @DBA" do editor SQL (spec 2026-10-30-agente-dba §4.9): o id da linha do
  // histórico vai no PRÓXIMO envio desta conversa, e só nele. O servidor relê a consulta e o erro.
  const [pendingConsultaSqlId, setPendingConsultaSqlId] = useState<number | undefined>(undefined)

  // Estado de resposta/citação
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null)

  // Próxima mensagem vai ao @Atendente, e não ao time. Armado pelo botão
  // "Responder ao @Atendente" numa mensagem do bot; zerado no envio.
  //
  // Sem isso, responder a uma pergunta do bot exige selecionar @Atendente na menção
  // outra vez, a cada mensagem — e quem não sabe disso escreve no canal do time e
  // não recebe resposta. Foi o que travou a abertura de chamado em 06/08/2026:
  // o bot perguntou "Posso abrir um chamado?" e as duas respostas seguintes saíram
  // como `POST /messages`. Ver docs/CHAT_TRIAGEM_CHAMADO_SPEC.md RF-002.
  const [respondendoAtendente, setRespondendoAtendente] = useState(false)
  // Armado por "Responder a {robô}" numa resposta de robô no grupo (consoleai#24585).
  const [respondendoRobo, setRespondendoRobo] = useState<AlvoRobo | null>(null)
  const armarAtendente = () => { setRespondendoRobo(null); setRespondendoAtendente(true) }
  const armarRobo = (alvo: AlvoRobo) => { setRespondendoAtendente(false); setRespondendoRobo(alvo) }
  const perguntarAoRoboDoGrupo = (botUserId: number, text: string) => {
    askRobo.mutate({ botUserId, text })
    setRespondendoRobo(null)
  }

  // -------------------------------------------------------------------------
  // (A) Consumo de location.state.scrollToMessageId (vindo da busca global).
  // Copia o id para um estado local `focusMessageId` e LIMPA o state da rota
  // (replace) para que um re-render não re-dispare o scroll.
  // -------------------------------------------------------------------------
  const [focusMessageId, setFocusMessageId] = useState<number | null>(null)
  useEffect(() => {
    if (embutida) return
    const state = location.state as { scrollToMessageId?: number } | null
    const target = state?.scrollToMessageId
    if (typeof target === 'number') {
      setFocusMessageId(target)
      // Consome o state: substitui a entrada de histórico sem o state.
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // (A2) Início do atendimento de chamado (spec 2026-09-26 §4.7): o botão do
  // chamado navega para cá com o pedido SÓ quando não há resumo. Dispara o
  // primeiro turno UMA vez e limpa o state (reload não repete). O ref por
  // `location.key` segura o duplo efeito do StrictMode.
  // -------------------------------------------------------------------------
  const inicioDisparadoRef = useRef<string | null>(null)
  useEffect(() => {
    if (embutida) return
    const pedido = (location.state as EstadoDeInicioDeAtendimento | null)?.iniciarAtendimentoChamado
    if (!pedido || inicioDisparadoRef.current === location.key) return
    inicioDisparadoRef.current = location.key
    navigate(location.pathname, { replace: true, state: null })
    askStream.start(textoDoInicioDeAtendimento(pedido.protocolo), { shared: true })
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // (A3) Finalizar chamado com IA (spec 2026-10-16 §4.4): mesmo mecanismo do resumo
  // inicial — dispara o pedido de fechamento UMA vez e limpa o state.
  const finalizarDisparadoRef = useRef<string | null>(null)
  useEffect(() => {
    if (embutida) return
    const pedido = (location.state as EstadoDeInicioDeAtendimento | null)?.finalizarChamado
    if (!pedido || finalizarDisparadoRef.current === location.key) return
    finalizarDisparadoRef.current = location.key
    navigate(location.pathname, { replace: true, state: null })
    askStream.start(textoDoFinalizarChamado(pedido.protocolo), { shared: true })
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // (A4) Texto pronto no campo, SEM enviar — vem da casca (`inicial`): a rota `/chat/:id` ou o
  // painel do editor. O id da consulta só vale para o próximo envio; um pedido sem id apaga o
  // anterior.
  useEffect(() => {
    if (!inicial?.prefill) return
    setPrefill(inicial.prefill)
    setPendingConsultaSqlId(inicial.consultaSqlId)
  }, [inicial?.chave]) // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // (B) Barra de busca dentro da conversa.
  // -------------------------------------------------------------------------
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery] = useDebouncedValue(searchQuery, 200)
  // Mensagens atualmente carregadas na lista visível (reportadas pelo MessageList).
  const [loadedMessages, setLoadedMessages] = useState<ChatMessage[]>([])
  // Ordem dos matches (in-view) e índice atual (navegação up/down).
  const [matchIds, setMatchIds] = useState<number[]>([])
  const [matchIndex, setMatchIndex] = useState<number>(-1)
  // Refs para preservar a posição da navegação através de recálculos.
  // - matchIdsRef: matches anteriores (para remapear por id no próximo cálculo).
  // - matchIndexRef: índice atual (sempre atualizado, sem re-render).
  // - prevQueryRef: última query calculada (distingue mudança-de-query de
  //   mudança-de-mensagens-ao-vivo).
  const matchIdsRef = useRef<number[]>([])
  const matchIndexRef = useRef<number>(-1)
  const prevQueryRef = useRef<string>('')
  useEffect(() => { matchIdsRef.current = matchIds }, [matchIds])
  useEffect(() => { matchIndexRef.current = matchIndex }, [matchIndex])

  // Recalcula os matches quando a query (debounced) ou as mensagens mudam.
  // Casamos contra as mensagens JÁ carregadas (mesma normalização da lib) para
  // que contador/navegação reflitam exatamente o que está na tela. Limitação:
  // ocorrências fora da janela carregada não entram no contador (YAGNI —
  // sem history-jump). Disparamos searchMessages() em paralelo apenas para
  // manter o store consistente; o contador usa os matches in-view.
  useEffect(() => {
    const q = debouncedQuery.trim()
    if (!searchOpen || !q) {
      setMatchIds([])
      setMatchIndex(-1)
      prevQueryRef.current = ''
      return
    }
    const ids = computeMatches(loadedMessages, debouncedQuery)
    const queryChanged = q !== prevQueryRef.current
    setMatchIds(ids)
    if (queryChanged) {
      // Nova busca: posiciona no PRIMEIRO match (topo dos resultados).
      setMatchIndex(ids.length > 0 ? 0 : -1)
    } else {
      // Mesma query, mensagens mudaram (ex.: mensagem ao vivo): preserva a
      // posição atual do usuário por id; senão, faz clamp na nova faixa.
      setMatchIndex(remapIndex(matchIdsRef.current, matchIndexRef.current, ids))
    }
    prevQueryRef.current = q
    // Best-effort: aquece o store (não afeta o contador in-view).
    void searchMessages(debouncedQuery, { conversationId: cid }).catch(() => {})
  }, [debouncedQuery, searchOpen, loadedMessages, cid])

  // Id da mensagem em foco: durante a busca é a ocorrência atual; fora dela é o
  // alvo do scroll global (A).
  const currentMatchId =
    searchOpen && matchIndex >= 0 && matchIndex < matchIds.length ? matchIds[matchIndex] : null
  const effectiveFocusId = currentMatchId ?? focusMessageId

  const gotoMatch = useCallback(
    (dir: 1 | -1) => {
      setMatchIndex((prev) => stepMatch(matchIds, prev, dir))
    },
    [matchIds],
  )

  // Consumo one-shot do latch de foco GLOBAL (A). Chamado pela MessageList após
  // rolar de fato o alvo. Só limpamos o `focusMessageId` global — a navegação da
  // busca in-view (currentMatchId → effectiveFocusId) NÃO é afetada, pois ela não
  // depende deste latch. Se o id consumido não for o global (é uma ocorrência da
  // busca), ignoramos.
  const handleFocusConsumed = useCallback(
    (id: number) => {
      setFocusMessageId((prev) => (prev === id ? null : prev))
    },
    [],
  )

  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    setSearchQuery('')
    setMatchIds([])
    setMatchIndex(-1)
    prevQueryRef.current = ''
  }, [])

  const searchBar = searchOpen ? (
    <Box px="md" pb="sm" pt={4} style={{ background: 'var(--mantine-color-body)', flexShrink: 0 }}>
      <Group gap="xs" wrap="nowrap" style={{ maxWidth: '100%' }}>
        <TextInput
          autoFocus
          flex={1}
          size="xs"
          placeholder="Buscar nesta conversa"
          leftSection={<IconSearch size={14} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeSearch()
            else if (e.key === 'Enter') gotoMatch(e.shiftKey ? -1 : 1)
          }}
          style={{ minWidth: 0 }}
          aria-label="Buscar nesta conversa"
        />
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {matchIds.length === 0 ? '0/0' : `${matchIndex + 1}/${matchIds.length}`}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          disabled={matchIds.length === 0}
          onClick={() => gotoMatch(-1)}
          aria-label="Ocorrência anterior"
        >
          <IconChevronUp size={16} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          disabled={matchIds.length === 0}
          onClick={() => gotoMatch(1)}
          aria-label="Próxima ocorrência"
        >
          <IconChevronDown size={16} />
        </ActionIcon>
        <CloseButton size="sm" onClick={closeSearch} aria-label="Fechar busca" />
      </Group>
    </Box>
  ) : null

  // Botão de lupa (alterna a barra de busca) — reutilizado nos dois cabeçalhos.
  const searchToggle = (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="md"
      onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
      aria-label={searchOpen ? 'Fechar busca' : 'Buscar mensagens'}
    >
      <IconSearch size={18} />
    </ActionIcon>
  )

  // Detector de tipo de conversa — lê envelope da primeira página de mensagens
  const primary = useConversationMessages(cid, undefined)
  const envelope = primary.data?.pages?.[0]?.conversation
  const convType = envelope?.conv_type

  // Mensagens de alerta — mesma queryKey usada pela aba "Alertas" (dedupe no
  // cache do react-query), usada só para resolver o título/trigger do alerta ao
  // pré-preencher o Composer via botão "Explicar".
  const alertsQuery = useConversationMessages(cid, 'alertas')
  // 'dba' (a conversa do banco aberto no editor) é conversa com o @Atendente, sem abas de alerta.
  const isHumanThread = convType === 'dm' || convType === 'org' || convType === 'chamado' || convType === 'dba'

  // -------------------------------------------------------------------------
  // Painel do @DevOps (spec 2026-11-04-painel-do-devops §3.2): os pedidos de Terraform
  // achados pelos cartões da conversa. A âncora de cada stage é o pedido mais recente
  // dele; o stage sai de `params.stage_chave` (ver `pedidosDaConversa.ts`).
  // -------------------------------------------------------------------------
  const cartoesDePedido = useMemo(() => {
    const todas = primary.data?.pages.flatMap((p) => p.items) ?? []
    if (isHumanThread) return cartoesDosPedidos([{ mensagens: todas }])
    // Com abas, o "Ir ao cartão" precisa abrir a aba da mensagem: a que está na leitura
    // da aba Alertas mora lá, o resto mora em Conversas (`chat_routes._classify`).
    const alertas = alertsQuery.data?.pages.flatMap((p) => p.items) ?? []
    const idsDeAlerta = new Set(alertas.map((m) => m.id))
    return cartoesDosPedidos([
      { aba: 'conversas', mensagens: todas.filter((m) => !idsDeAlerta.has(m.id)) },
      { aba: 'alertas', mensagens: alertas },
    ])
  }, [primary.data, alertsQuery.data, isHumanThread])
  const ancorasDosStages = useMemo(() => ancorasPorStage(cartoesDePedido), [cartoesDePedido])
  const pedidosComCartao = useMemo(() => new Set(cartoesDePedido.keys()), [cartoesDePedido])
  const [painelDoRunnerAberto, setPainelDoRunnerAberto] = useState(false)
  const [stageDoPainel, setStageDoPainel] = useState<string | null>(null)
  const valorDoStage = (a: AncoraDoStage) => a.stage ?? `#${a.pedidoId}`
  // Sem escolha (ou com a escolha fora desta conversa), o stage do pedido mais recente.
  const ancoraDoPainel = ancorasDosStages.find((a) => valorDoStage(a) === stageDoPainel)
    ?? ancorasDosStages[0] ?? null

  // "Ir ao cartão" é navegação (D-5): abre a aba da mensagem e usa o mesmo foco da busca
  // global (rola + pisca). Não muda estado do pedido nem chama API.
  const irAoCartao = useCallback((pedidoId: number) => {
    const cartao = cartoesDePedido.get(pedidoId)
    if (!cartao) return
    // A ocorrência da busca tem precedência sobre o foco: fechar a busca deixa o foco valer.
    if (searchOpen) closeSearch()
    if (cartao.aba) setActiveTab(cartao.aba)
    setFocusMessageId(cartao.mensagemId)
  }, [cartoesDePedido, searchOpen, closeSearch])

  // O painel do @DBA embute esta conversa num painel de 420 px: lá não cabe outro ao lado.
  const botaoDoRunner = !embutida && cartoesDePedido.size > 0 ? (
    <Button
      size="compact-sm"
      variant={painelDoRunnerAberto ? 'light' : 'subtle'}
      color="inovareTeal"
      leftSection={<IconServerCog size={16} />}
      onClick={() => setPainelDoRunnerAberto((v) => !v)}
      aria-pressed={painelDoRunnerAberto}
    >
      Andamento do runner
    </Button>
  ) : null

  const painelDoRunner = botaoDoRunner && painelDoRunnerAberto && ancoraDoPainel ? (
    // Ao lado, como o `PainelDoDba` ao lado do editor (`QueryRunnerTab`).
    <Box w={420} p="xs" style={{ flexShrink: 0, position: 'sticky', top: 0, height: '100%', minHeight: 0 }}>
      <Paper withBorder radius="sm" p="sm" h="100%" style={{ overflowY: 'auto' }} data-painel-do-runner>
        {ancorasDosStages.length > 1 && (
          <Select
            label="Stage"
            size="xs"
            mb="sm"
            allowDeselect={false}
            data={ancorasDosStages.map((a) => ({
              value: valorDoStage(a), label: a.stage ?? `pedido #${a.pedidoId}`,
            }))}
            value={valorDoStage(ancoraDoPainel)}
            onChange={(v) => setStageDoPainel(v)}
          />
        )}
        <PainelDoDevOps
          key={ancoraDoPainel.pedidoId}
          pedidoId={ancoraDoPainel.pedidoId}
          pedidosComCartao={pedidosComCartao}
          onIrAoCartao={irAoCartao}
        />
      </Paper>
    </Box>
  ) : null

  // A conversa e o painel lado a lado. A casca é a mesma com o painel fechado: abrir e
  // fechar não remonta a conversa (o texto no campo e o scroll ficam).
  const comPainelDoRunner = (conversa: ReactNode) => (
    <Group h="100%" gap={0} wrap="nowrap" align="stretch" style={{ minHeight: 0 }}>
      <Box style={{ flex: 1, minWidth: 0, height: '100%' }}>{conversa}</Box>
      {painelDoRunner}
    </Group>
  )

  // Silenciar (apenas para threads humanas)
  const [muted, setMuted] = useState(false)
  const toggleMute = useToggleMute(cid)

  // Ocultar conversa
  const [hideConvOpened, { open: openHideConv, close: closeHideConv }] = useDisclosure(false)
  const hideConversation = useHideConversation(cid)
  // Recomeçar com o @Atendente (#24784)
  const recomecar = useRecomecarAtendente(cid)

  // Avatar de grupo — upload
  const avatarFileInputRef = useRef<HTMLInputElement>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)

  async function handleGroupAvatarPick(file: File) {
    setAvatarUploading(true)
    try {
      const presign = await chatApi.convAvatarPresign(cid, {
        filename: file.name,
        mimeType: file.type || 'image/jpeg',
        sizeBytes: file.size,
      })
      if (!presign.supported || !presign.presigned_put_url || !presign.s3_key) {
        notifications.show({
          color: 'red',
          title: 'Upload não suportado',
          message: 'O servidor não suporta upload de avatar no momento.',
        })
        return
      }
      await chatApi.uploadToS3(presign.presigned_put_url, file)
      await chatApi.convAvatarConfirm(cid, presign.s3_key)
      // Força recarregar o envelope (mensagens) para exibir o novo avatar
      await primary.refetch()
      notifications.show({ color: 'teal', title: 'Imagem atualizada', message: 'Avatar do grupo atualizado com sucesso.' })
    } catch {
      notifications.show({ color: 'red', title: 'Erro no upload', message: 'Não foi possível atualizar a imagem do grupo. Tente novamente.' })
    } finally {
      setAvatarUploading(false)
    }
  }

  function handleConfirmHideConversation() {
    hideConversation.mutate(true, {
      onSuccess: () => {
        closeHideConv()
        voltar?.acao()
      },
    })
  }

  // Sincroniza estado de mute com o envelope assim que ele chegar do backend
  useEffect(() => {
    if (envelope?.is_muted !== undefined) setMuted(!!envelope.is_muted)
  }, [envelope?.is_muted])

  // Resolução do título:
  //   - thread humana: usa envelope.title (DM = nome do outro participante).
  //     Fallback para o título da lista (que já resolve o peer) e depois #cid —
  //     `||` cobre o caso de title vazio vindo de backend antigo.
  //   - alerta: usa o grupo da lista de conversas (comportamento original)
  const group = Array.isArray(conversations)
    ? conversations.find((c) => c.id === cid)
    : undefined
  const title = isHumanThread
    ? (envelope?.title || group?.title || `#${cid}`)
    : (group?.title ?? `#${cid}`)

  // "Explicar" num AlertBubble → muda para a aba Atendente e JÁ ENVIA uma
  // pergunta completa sobre o alerta (conversa ativa na hora). O vínculo com o
  // alerta vai no `alert_message_id` → o backend injeta o contexto do alerta.
  function handleExplainAlert(alertMsgId: number) {
    // Resolve o alerta correspondente no cache para montar a pergunta.
    const alertMsg = alertsQuery.data?.pages
      ?.flatMap((p) => p.items)
      .find((m) => m.id === alertMsgId)
    const question = buildAlertQuestion({
      body: alertMsg?.body,
      metadata: alertMsg?.metadata as { trigger?: unknown; host?: unknown } | null,
    })

    // Aba "Atendente IA" aposentada — o Atendente responde inline na aba
    // Conversas, de forma COMPARTILHADA (visível a todos), como um humano.
    setActiveTab('conversas')
    askStream.start(question, { alertMessageId: alertMsgId, shared: true })
  }

  // ---------------------------------------------------------------------------
  // Bloco de cabeçalho compartilhado (reutilizado nas duas ramificações)
  // ---------------------------------------------------------------------------

  // O botão do cabeçalho é da casca: a rota volta à lista, o painel do editor fecha.
  const botaoVoltar = voltar ? (
    <ActionIcon variant="subtle" color="gray" size="md" onClick={voltar.acao} aria-label={voltar.rotulo}>
      {voltar.icone ?? <IconArrowLeft size={18} />}
    </ActionIcon>
  ) : null

  // Cabeçalho para threads humanas (dm/org)
  const canManageAvatar = convType === 'org' && envelope?.can_manage_avatar === true

  const humanHeader = (
    <Box
      px="md"
      py="sm"
      style={{
        borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
        background: 'var(--mantine-color-body)',
        flexShrink: 0,
      }}
    >
      {/* Input oculto para troca de avatar */}
      <input
        ref={avatarFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleGroupAvatarPick(file)
          // Reset para permitir re-selecionar o mesmo arquivo
          e.target.value = ''
        }}
      />

      <Group gap="sm" align="center">
        {botaoVoltar}

        {/* Avatar da conversa */}
        <Box style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            src={envelope?.avatar_url ?? undefined}
            radius="xl"
            size={34}
            color="teal"
          >
            {getInitials(title)}
          </Avatar>
          {canManageAvatar && (
            <Tooltip label="Trocar imagem" withArrow position="bottom">
              <ActionIcon
                size="xs"
                radius="xl"
                color="teal"
                variant="filled"
                loading={avatarUploading}
                onClick={() => avatarFileInputRef.current?.click()}
                aria-label="Trocar imagem do grupo"
                style={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 16,
                  height: 16,
                  minWidth: 0,
                }}
              >
                <IconCamera size={10} />
              </ActionIcon>
            </Tooltip>
          )}
        </Box>

        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} size="sm" lineClamp={1}>
            {title}
          </Text>
          {envelope?.is_chamado && envelope.task_request_id != null && (
            <Anchor component={Link} to={`/chamados/${envelope.task_request_id}`} size="xs">
              {`Chamado ${envelope.protocolo ?? `#${envelope.task_request_id}`}`}
            </Anchor>
          )}
        </Stack>

        {/* O painel do @DevOps: só com pedido de Terraform na conversa */}
        {botaoDoRunner}

        {/* Busca dentro da conversa */}
        {searchToggle}

        {/* Botão de integrantes — apenas para grupos (org) */}
        {convType === 'org' && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => setMembersOpen(true)}
            aria-label="Ver integrantes"
          >
            <IconUsers size={18} />
          </ActionIcon>
        )}

        {/* Menu de opções */}
        <Menu withinPortal position="bottom-end" shadow="md">
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" size="md" aria-label="Opções">
              <IconDots size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {muted ? (
              <Menu.Item
                leftSection={<IconBell size={16} />}
                onClick={() =>
                  toggleMute.mutate({ muted: false }, { onSuccess: () => setMuted(false) })
                }
              >
                Ativar notificações
              </Menu.Item>
            ) : (
              <>
                <Menu.Label>
                  <Group gap={6}><IconBellOff size={14} /> Silenciar por</Group>
                </Menu.Label>
                {([
                  ['8h', '8 horas'],
                  ['24h', '24 horas'],
                  ['7d', '7 dias'],
                  ['forever', 'Sempre'],
                ] as const).map(([dur, label]) => (
                  <Menu.Item
                    key={dur}
                    onClick={() =>
                      toggleMute.mutate(
                        { muted: true, duration: dur },
                        { onSuccess: () => setMuted(true) },
                      )
                    }
                  >
                    {label}
                  </Menu.Item>
                ))}
              </>
            )}
            {/* O Robô tem histórico próprio, que o marcador não corta. */}
            {!envelope?.is_robo_dm && recomecar.item}
            <Menu.Item
              leftSection={<IconArchive size={16} />}
              color="orange"
              onClick={openHideConv}
            >
              Ocultar conversa
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        {/* Indicador "online" */}
        <Group gap={4} align="center">
          <Box
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--ai-brand-verde)',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />
          <Text size="xs" c="dimmed">online</Text>
        </Group>
      </Group>
    </Box>
  )

  // ---------------------------------------------------------------------------
  // Estado de carregamento — aguarda o envelope antes de decidir o branch
  // ---------------------------------------------------------------------------

  if (primary.isLoading && convType === undefined) {
    return (
      <Stack h="100%" gap={0} style={{ minHeight: 0 }}>
        {/* Cabeçalho genérico enquanto carrega */}
        <Box
          px="md"
          py="sm"
          style={{
            borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
            background: 'var(--mantine-color-body)',
            flexShrink: 0,
          }}
        >
          <Group gap="sm" align="center">
            {botaoVoltar}
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm" lineClamp={1}>
                {group?.title ?? `#${cid}`}
              </Text>
            </Stack>
          </Group>
        </Box>
        <Group justify="center" align="center" style={{ flex: 1 }}>
          <Loader size="sm" type="dots" color="teal" />
        </Group>
      </Stack>
    )
  }

  // ---------------------------------------------------------------------------
  // Branch: thread humana (dm ou org) — sem abas
  // ---------------------------------------------------------------------------

  if (isHumanThread) {
    // A conversa com o robô é uma DM como as outras, com o portão antes (sem robô,
    // robô sem nome), a linha de estado e a gaveta de alertas (spec 2026-10-02).
    const roboDm = !!envelope?.is_robo_dm
    const perguntarAoRobo = (text: string, attachments: AskAttachmentRef[] = []) =>
      roboAsk.mutate({ text, attachments })
    const conteudo = (robo: Robo | null) => (
      <Stack h="100%" gap={0} style={{ minHeight: 0 }}>
        {recomecar.dialogo}
        {/* Modal de confirmação de ocultar conversa */}
        <Modal
          opened={hideConvOpened}
          onClose={closeHideConv}
          title="Ocultar conversa"
          centered
          size="sm"
        >
          <Text size="sm">
            Esta conversa será ocultada da sua lista. Você pode encontrá-la novamente se receber uma nova mensagem.
          </Text>
          <Group justify="flex-end" mt="md" gap="sm">
            <Button variant="subtle" color="gray" onClick={closeHideConv}>
              Cancelar
            </Button>
            <Button
              color="orange"
              onClick={handleConfirmHideConversation}
              loading={hideConversation.isPending}
            >
              Ocultar
            </Button>
          </Group>
        </Modal>

        {humanHeader}
        {robo && <RoboDmTopo robo={robo} cid={cid} />}
        {searchBar}

        {/* Thread único sem abas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <MessageList
            cid={cid}
            tab={undefined}
            onReply={(m) => setReplyingTo(m)}
            // Na DM do robô toda mensagem já vai a ele: sem "Responder ao @Atendente".
            onReplyToAtendente={robo ? undefined : armarAtendente}
            onReplyToRobo={robo ? undefined : armarRobo}
            assistantSteps={
              robo ? (roboAsk.isPending ? [] : null) : askStream.streaming ? askStream.steps : null
            }
            focusMessageId={effectiveFocusId}
            onMessagesLoaded={setLoadedMessages}
            matchIds={matchIds}
            onFocusConsumed={handleFocusConsumed}
          />
        </div>

        {typingText && (
          <Text size="xs" c="dimmed" px="md" pb={4} style={{ fontStyle: 'italic' }}>
            {typingText}
          </Text>
        )}

        {envelope?.is_chamado && !askStream.streaming &&
          !loadedMessages.some((m) => m.kind === 'bot') && (
          // O turno do @Atendente falhou (ou ainda não houve): repete o disparo
          // da §4.7 com o mesmo texto fixo (spec §4.8).
          <Group px="md" pb={4}>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconSparkles size={14} />}
              onClick={() =>
                askStream.start(
                  textoDoInicioDeAtendimento(envelope.protocolo ?? `#${envelope.task_request_id}`),
                  { shared: true },
                )
              }
            >
              Gerar resumo de novo
            </Button>
          </Group>
        )}
        {robo && primary.data?.pages?.[0]?.items.length === 0 && !roboAsk.isPending && (
          // Conversa vazia: os atalhos da tela antiga viram sugestões.
          <Group gap="xs" px="md" pb={6}>
            {[`Bom dia, ${robo.name}!`, 'Quais são os alertas mais críticos?', 'Resumo do ambiente', 'Como você pode ajudar?'].map((q) => (
              <Button key={q} size="xs" variant="light" onClick={() => perguntarAoRobo(q)}>
                {q}
              </Button>
            ))}
          </Group>
        )}
        {robo && roboAsk.data?.interaction?.status === 'unavailable' && (
          <Alert color="orange" mx="md" mb={6}>
            A resposta foi salva, mas não foi possível sincronizar esta interação com o robô.
          </Alert>
        )}
        {robo ? (
          <Composer
            cid={cid}
            activeTab="conversas"
            onAsk={(text, _alertMsgId, _shared, attachments) => perguntarAoRobo(text, attachments ?? [])}
            sendingAsk={roboAsk.isPending}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            forceAtendente
            destino={robo.name}
            initialText={prefill}
            attachmentsSupported={envelope?.attachments_supported ?? false}
          />
        ) : (
          <Composer
            cid={cid}
            activeTab="conversas"
            onAsk={(text, _alertMsgId, shared, attachments) => {
              // O DM com o @Atendente é `conv_type = 'dm'` e cai AQUI, não no ramo de alerta:
              // sem o `consultaSqlId`, o "Perguntar ao @DBA" do editor chegava ao servidor sem
              // o id, e a âncora (SQL, erro, credencial relidos do histórico) nunca valia.
              askStream.start(text, { shared, attachments, consultaSqlId: pendingConsultaSqlId })
              setPrefill(undefined)
              setPendingConsultaSqlId(undefined)
              setRespondendoAtendente(false)
            }}
            initialText={prefill}
            // O robô de um colega mora no grupo de Organização (TASK-12125): sem
            // isto a menção a ele virava mensagem comum e ninguém respondia.
            onAskRobo={perguntarAoRoboDoGrupo}
            sendingAsk={askStream.streaming || askRobo.isPending}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            forceAtendente={mandaAoAtendente(envelope) || respondendoAtendente}
            responderAoRobo={respondendoRobo}
            attachmentsSupported={
              (envelope?.attachments_supported ?? false) || mandaAoAtendente(envelope)
            }
          />
        )}

        <MembersDrawer
          cid={cid}
          opened={membersOpen}
          onClose={() => setMembersOpen(false)}
        />
      </Stack>
    )
    return comPainelDoRunner(
      roboDm ? <RoboDmPortao cid={cid}>{(robo) => conteudo(robo)}</RoboDmPortao> : conteudo(null),
    )
  }

  // ---------------------------------------------------------------------------
  // Branch: conversa de alerta — UI original com três abas (inalterada)
  // ---------------------------------------------------------------------------

  return comPainelDoRunner(
    <Stack h="100%" gap={0} style={{ minHeight: 0 }}>
      {recomecar.dialogo}
      {/* Modal de confirmação de ocultar conversa */}
      <Modal
        opened={hideConvOpened}
        onClose={closeHideConv}
        title="Ocultar conversa"
        centered
        size="sm"
      >
        <Text size="sm">
          Esta conversa será ocultada da sua lista. Você pode encontrá-la novamente se receber uma nova mensagem.
        </Text>
        <Group justify="flex-end" mt="md" gap="sm">
          <Button variant="subtle" color="gray" onClick={closeHideConv}>
            Cancelar
          </Button>
          <Button
            color="orange"
            onClick={handleConfirmHideConversation}
            loading={hideConversation.isPending}
          >
            Ocultar
          </Button>
        </Group>
      </Modal>

      {/* Cabeçalho */}
      <Box
        px="md"
        py="sm"
        style={{
          borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
          background: 'var(--mantine-color-body)',
          flexShrink: 0,
        }}
      >
        <Group gap="sm" align="center">
          {botaoVoltar}

          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" lineClamp={1}>
              {title}
            </Text>
          </Stack>

          {/* O painel do @DevOps: só com pedido de Terraform na conversa */}
          {botaoDoRunner}

          {/* Busca dentro da conversa */}
          {searchToggle}

          {/* Botão de integrantes */}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => setMembersOpen(true)}
            aria-label="Ver integrantes"
          >
            <IconUsers size={18} />
          </ActionIcon>

          {/* Menu de opções */}
          <Menu withinPortal position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="md" aria-label="Opções">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {recomecar.item}
              <Menu.Item
                leftSection={<IconArchive size={16} />}
                color="orange"
                onClick={openHideConv}
              >
                Ocultar conversa
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {/* Indicador "online" */}
          <Group gap={4} align="center">
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--ai-brand-verde)',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />
            <Text size="xs" c="dimmed">online</Text>
          </Group>
        </Group>
      </Box>

      {searchBar}

      {/* Abas */}
      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as ChatTab)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        styles={{
          root: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
          panel: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 },
        }}
      >
        <Tabs.List
          style={{
            borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
            flexShrink: 0,
          }}
          px="md"
        >
          <Tabs.Tab value="alertas">Alertas</Tabs.Tab>
          <Tabs.Tab value="conversas">Conversas</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="alertas">
          <MessageList
            cid={cid}
            tab="alertas"
            active={activeTab === 'alertas'}
            onExplainAlert={handleExplainAlert}
            onReply={(m) => setReplyingTo(m)}
            focusMessageId={activeTab === 'alertas' ? effectiveFocusId : null}
            onMessagesLoaded={activeTab === 'alertas' ? setLoadedMessages : undefined}
            matchIds={activeTab === 'alertas' ? matchIds : undefined}
            onFocusConsumed={activeTab === 'alertas' ? handleFocusConsumed : undefined}
          />
        </Tabs.Panel>

        <Tabs.Panel value="conversas">
          <MessageList
            cid={cid}
            tab="conversas"
            active={activeTab === 'conversas'}
            onReply={(m) => setReplyingTo(m)}
            // A recomendação do @DevOps é kind='bot' sem `channel` explícito —
            // chat_routes.py:_classify cai na regra 5 e a categoriza 'conversas',
            // não 'alertas'. Sem este prop aqui, o botão de "Explicar" nunca
            // teria onExplainAlert disponível para ela, mesmo com a condição de
            // Conversation.tsx:393 correta (TASK-10542).
            onExplainAlert={handleExplainAlert}
            onReplyToAtendente={armarAtendente}
            onReplyToRobo={armarRobo}
            // Aba "Atendente IA" aposentada — o bot responde AQUI (Conversas);
            // os passos ao vivo aparecem inline enquanto ele processa.
            assistantSteps={activeTab === 'conversas' && askStream.streaming ? askStream.steps : null}
            focusMessageId={activeTab === 'conversas' ? effectiveFocusId : null}
            onMessagesLoaded={activeTab === 'conversas' ? setLoadedMessages : undefined}
            matchIds={activeTab === 'conversas' ? matchIds : undefined}
            onFocusConsumed={activeTab === 'conversas' ? handleFocusConsumed : undefined}
          />
        </Tabs.Panel>
      </Tabs>

      {activeTab === 'conversas' && typingText && (
        <Text size="xs" c="dimmed" px="md" pb={4} style={{ fontStyle: 'italic' }}>
          {typingText}
        </Text>
      )}

      {/* Compositor — comportamento varia por aba */}
      <Composer
        cid={cid}
        activeTab={activeTab}
        onAskRobo={perguntarAoRoboDoGrupo}
        onAsk={(text, alertMsgId, shared, attachments) => {
          // Envio ao @Atendente com streaming ao vivo dos passos. O alertMsgId
          // (quando houver) preserva o contexto do alerta no backend.
          askStream.start(text, {
            alertMessageId: alertMsgId, shared, attachments, consultaSqlId: pendingConsultaSqlId,
          })
          setPrefill(undefined)
          setPendingAlertMessageId(undefined)
          setPendingConsultaSqlId(undefined)
          setRespondendoAtendente(false)
        }}
        sendingAsk={askStream.streaming || askRobo.isPending}
        initialText={prefill}
        alertMessageId={pendingAlertMessageId}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
        // Armado pelo botão "Responder ao @Atendente" numa mensagem do bot: a
        // próxima mensagem vai a ele, e o placeholder do composer diz isso.
        forceAtendente={respondendoAtendente}
        responderAoRobo={respondendoRobo}
        attachmentsSupported={envelope?.attachments_supported ?? false}
      />

      {/* Painel de integrantes */}
      <MembersDrawer
        cid={cid}
        opened={membersOpen}
        onClose={() => setMembersOpen(false)}
      />
    </Stack>
  )
}
